// @givmo/board — how a folder's files reach Board's uploads (CBG-AI5-INBOX-WEB, D-CBG-41; design-284
// `scratchpad/2026-10-24-import-inbox-design/design.md` §1, §3, frame u3).
//
// Two browser seams, kept apart from the page so each reads plainly:
//
//   * THE FILES OF A DROP. A dropped folder arrives as directory entries, never as files: each is
//     walked to its files (every level, every read batch), skipping hidden ones (a Mac folder always
//     holds a `.DS_Store`). The entries must be taken while the drop event lasts, so the walk's
//     first, synchronous step takes them all before anything awaits.
//   * A FILE'S PUT, WITH ITS PROGRESS. The bytes go straight to the person's slot in storage, carrying
//     no Givmo credential, with exactly the headers the slot names: its signature binds them, and a
//     header left out reads as a 403 (the same rule as `putToSlot`). `fetch` reports no upload
//     progress, so this is an `XMLHttpRequest`, whose upload events give the row its percentage.

import type { BoardFileUploadSlotOut } from '@givmo/api-client';

/** How long one file's PUT may take before it reads as not uploaded. */
const PUT_TIMEOUT_MS = 30 * 60 * 1000;

/** Board's limit on a file's name; a folder path longer than it falls back to the file's own. */
const NAME_MAX_LENGTH = 255;

/** One file as the page adds it: the file, and the name it goes up under. */
export type ChosenFile = { file: File; name: string };

/** Whether a file or folder name is a hidden one, which a person never meant to upload. */
function isHidden(name: string): boolean {
  return name.startsWith('.');
}

/**
 * The name a file goes up under (cut inbox's review fold, M8): its path inside the folder dropped
 * or chosen, that folder itself left out, so a "Minutes.pdf" in 2019/ and another in 2020/ read
 * apart on the page and to the AI. A file dropped or chosen on its own keeps its own name.
 */
function nameWithin(path: string | undefined, file: File): string {
  const segments = (path ?? '').split('/').filter((segment) => segment.length > 0);
  const inside = segments.length > 1 ? segments.slice(1).join('/') : file.name;
  return inside.length <= NAME_MAX_LENGTH ? inside : file.name;
}

function readEntries(reader: FileSystemDirectoryReader): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => reader.readEntries(resolve, reject));
}

function fileOf(entry: FileSystemFileEntry): Promise<File> {
  return new Promise((resolve, reject) => entry.file(resolve, reject));
}

async function walk(entry: FileSystemEntry, into: ChosenFile[]): Promise<void> {
  if (isHidden(entry.name)) return;
  if (entry.isFile) {
    const file = await fileOf(entry as FileSystemFileEntry);
    into.push({ file, name: nameWithin(entry.fullPath, file) });
    return;
  }
  if (!entry.isDirectory) return;
  const reader = (entry as FileSystemDirectoryEntry).createReader();
  // A directory reads in batches (Chromium's are 100 entries): read until a batch comes back empty.
  for (;;) {
    const batch = await readEntries(reader);
    if (batch.length === 0) return;
    for (const child of batch) await walk(child, into);
  }
}

/**
 * Every file a drop carries, a dropped folder's at every level, hidden files left out. Call it from
 * the drop handler itself: the entries are taken before it first awaits.
 */
export function filesFromDrop(dataTransfer: DataTransfer): Promise<ChosenFile[]> {
  const entries = Array.from(dataTransfer.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.webkitGetAsEntry?.() ?? null);
  const plain = Array.from(dataTransfer.files ?? []);
  return (async () => {
    if (entries.length === 0 || entries.some((entry) => entry === null)) {
      return plain
        .filter((file) => !isHidden(file.name))
        .map((file) => ({ file, name: file.name }));
    }
    const files: ChosenFile[] = [];
    for (const entry of entries) if (entry !== null) await walk(entry, files);
    return files;
  })();
}

/**
 * The files a file input chose: a chosen folder's at every level (`webkitdirectory`), each named
 * with its folder, or files chosen one by one. Hidden files, and any file inside a hidden folder,
 * are left out as the drop leaves them out (a Mac folder always holds a `.DS_Store`).
 */
export function filesFromInput(list: FileList | null | undefined): ChosenFile[] {
  return Array.from(list ?? [])
    .filter((file) => !(file.webkitRelativePath || file.name).split('/').some(isHidden))
    .map((file) => ({ file, name: nameWithin(file.webkitRelativePath || file.name, file) }));
}

/**
 * PUT a file to a person's slot with exactly its headers, reporting the share of its bytes sent
 * (0–99: the last percent is storage's answer). True when storage took it.
 */
export function putWithProgress(
  slot: BoardFileUploadSlotOut,
  file: Blob,
  onProgress: (percent: number) => void,
): Promise<boolean> {
  return new Promise((resolve) => {
    const request = new XMLHttpRequest();
    request.open(slot.method ?? 'PUT', slot.url);
    for (const [name, value] of Object.entries(slot.headers ?? {})) {
      if (typeof value === 'string') request.setRequestHeader(name, value);
    }
    request.upload.onprogress = (event) => {
      if (event.lengthComputable && event.total > 0) {
        onProgress(Math.min(99, Math.floor((event.loaded / event.total) * 100)));
      }
    };
    // A PUT that never ends would hold one of the page's few upload slots for good: past half an
    // hour (50 MB at well under 1 Mbit/s) it reads as not uploaded, for "Try again".
    request.timeout = PUT_TIMEOUT_MS;
    request.onload = () => resolve(request.status >= 200 && request.status < 300);
    request.onerror = () => resolve(false);
    request.onabort = () => resolve(false);
    request.ontimeout = () => resolve(false);
    request.send(file);
  });
}
